import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { 
  IndianRupee, 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Receipt,
  Building2,
  Wallet,
  Download,
  Plus,
  Edit2,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  Calendar,
  FileText,
  RefreshCw,
  X,
  Save,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Modal wrapper component using React Portal
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-lg text-slate-800">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-full hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};

const FinanceModule = () => {
  const [activeView, setActiveView] = useState('overview');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [salaries, setSalaries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [candidateCharges, setCandidateCharges] = useState([]);
  const [incentivesSummary, setIncentivesSummary] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Modal states
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [salaryForm, setSalaryForm] = useState({ user_email: '', user_name: '', monthly_salary: '', effective_from: '', notes: '' });
  const [expenseForm, setExpenseForm] = useState({ category: 'electricity', description: '', amount: '', month: selectedMonth, notes: '' });
  const [revenueForm, setRevenueForm] = useState({ source: 'other', description: '', amount: '', month: selectedMonth, notes: '' });
  const [chargeForm, setChargeForm] = useState({ 
    candidate_email: '', 
    candidate_name: '', 
    candidate_phone: '',
    charge_type: 'initial_charge', 
    amount: '', 
    status: 'pending', 
    notes: '',
    additional_info: ''
  });
  const [candidates, setCandidates] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [showCandidateDropdown, setShowCandidateDropdown] = useState(false);

  const getAuthToken = () => localStorage.getItem('token');
  const getConfig = useCallback(() => ({
    headers: { Authorization: `Bearer ${getAuthToken()}` }
  }), []);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/dashboard`, getConfig());
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
    }
  }, [getConfig]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/summary/${selectedMonth}`, getConfig());
      setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [selectedMonth, getConfig]);

  const fetchSalaries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/salaries`, getConfig());
      setSalaries(res.data);
    } catch (err) {
      console.error('Failed to fetch salaries:', err);
    }
  }, [getConfig]);

  const fetchExpenses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/expenses?month=${selectedMonth}`, getConfig());
      setExpenses(res.data);
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
    }
  }, [selectedMonth, getConfig]);

  const fetchRevenue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/revenue?month=${selectedMonth}`, getConfig());
      setRevenue(res.data);
    } catch (err) {
      console.error('Failed to fetch revenue:', err);
    }
  }, [selectedMonth, getConfig]);

  const fetchCandidateCharges = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/candidate-charges?month=${selectedMonth}`, getConfig());
      setCandidateCharges(res.data);
    } catch (err) {
      console.error('Failed to fetch charges:', err);
    }
  }, [selectedMonth, getConfig]);

  const fetchIncentivesSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/incentives/summary?month=${selectedMonth}`, getConfig());
      setIncentivesSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch incentives:', err);
    }
  }, [selectedMonth, getConfig]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, getConfig());
      setUsers(res.data.filter(u => u.role !== 'candidate'));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [getConfig]);

  const fetchCandidates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/candidates`, getConfig());
      setCandidates(res.data || []);
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
    }
  }, [getConfig]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchDashboard(),
      fetchSummary(),
      fetchSalaries(),
      fetchExpenses(),
      fetchRevenue(),
      fetchCandidateCharges(),
      fetchIncentivesSummary(),
      fetchUsers(),
      fetchCandidates()
    ]).finally(() => setLoading(false));
  }, [fetchDashboard, fetchSummary, fetchSalaries, fetchExpenses, fetchRevenue, fetchCandidateCharges, fetchIncentivesSummary, fetchUsers, fetchCandidates]);

  const handleMonthChange = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  // CRUD handlers
  const handleSaveSalary = async () => {
    try {
      await axios.post(`${API}/finance/salaries`, salaryForm, getConfig());
      toast.success('Salary saved successfully');
      setShowSalaryModal(false);
      setSalaryForm({ user_email: '', user_name: '', monthly_salary: '', effective_from: '', notes: '' });
      fetchSalaries();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save salary');
    }
  };

  const handleDeleteSalary = async (email) => {
    if (!window.confirm('Are you sure you want to delete this salary record?')) return;
    try {
      await axios.delete(`${API}/finance/salaries/${encodeURIComponent(email)}`, getConfig());
      toast.success('Salary deleted');
      fetchSalaries();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete salary');
    }
  };

  const handleSaveExpense = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/finance/expenses/${editingItem.id}`, expenseForm, getConfig());
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/finance/expenses`, { ...expenseForm, month: selectedMonth }, getConfig());
        toast.success('Expense added');
      }
      setShowExpenseModal(false);
      setEditingItem(null);
      setExpenseForm({ category: 'electricity', description: '', amount: '', month: selectedMonth, notes: '' });
      fetchExpenses();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/finance/expenses/${id}`, getConfig());
      toast.success('Expense deleted');
      fetchExpenses();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete expense');
    }
  };

  const handleSaveRevenue = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/finance/revenue/${editingItem.id}`, revenueForm, getConfig());
        toast.success('Revenue updated');
      } else {
        await axios.post(`${API}/finance/revenue`, { ...revenueForm, month: selectedMonth }, getConfig());
        toast.success('Revenue added');
      }
      setShowRevenueModal(false);
      setEditingItem(null);
      setRevenueForm({ source: 'other', description: '', amount: '', month: selectedMonth, notes: '' });
      fetchRevenue();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save revenue');
    }
  };

  const handleDeleteRevenue = async (id) => {
    if (!window.confirm('Delete this revenue entry?')) return;
    try {
      await axios.delete(`${API}/finance/revenue/${id}`, getConfig());
      toast.success('Revenue deleted');
      fetchRevenue();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete revenue');
    }
  };

  const handleSaveCharge = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/finance/candidate-charges/${editingItem.id}`, chargeForm, getConfig());
        toast.success('Charge updated');
      } else {
        await axios.post(`${API}/finance/candidate-charges`, chargeForm, getConfig());
        toast.success('Charge added');
      }
      setShowChargeModal(false);
      setEditingItem(null);
      setChargeForm({ 
        candidate_email: '', 
        candidate_name: '', 
        candidate_phone: '',
        charge_type: 'initial_charge', 
        amount: '', 
        status: 'pending', 
        notes: '',
        additional_info: ''
      });
      setCandidateSearch('');
      fetchCandidateCharges();
      fetchSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save charge');
    }
  };

  const handleSelectCandidate = (candidate) => {
    setChargeForm({
      ...chargeForm,
      candidate_email: candidate.email,
      candidate_name: candidate.name,
      candidate_phone: candidate.phone || ''
    });
    setCandidateSearch(candidate.name);
    setShowCandidateDropdown(false);
  };

  const filteredCandidates = candidates.filter(c => 
    c.name?.toLowerCase().includes(candidateSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(candidateSearch.toLowerCase()) ||
    c.phone?.includes(candidateSearch)
  ).slice(0, 10);

  const handleDeleteCharge = async (id) => {
    if (!window.confirm('Delete this charge?')) return;
    try {
      await axios.delete(`${API}/finance/candidate-charges/${id}`, getConfig());
      toast.success('Charge deleted');
      fetchCandidateCharges();
      fetchSummary();
    } catch (err) {
      toast.error('Failed to delete charge');
    }
  };

  const handleDownloadReport = async () => {
    try {
      const response = await axios.get(`${API}/finance/report/${selectedMonth}/csv`, {
        ...getConfig(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `finance_report_${selectedMonth}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  const expenseCategories = [
    { value: 'electricity', label: 'Electricity' },
    { value: 'wifi', label: 'Wi-Fi / Internet' },
    { value: 'rent', label: 'Rent' },
    { value: 'website', label: 'Website / Hosting' },
    { value: 'software', label: 'Software / Tools' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'travel', label: 'Travel' },
    { value: 'other', label: 'Other' }
  ];

  const revenueSources = [
    { value: 'candidate_charges', label: 'Candidate Charges' },
    { value: 'employer_fees', label: 'Employer Fees' },
    { value: 'placement_fees', label: 'Placement Fees' },
    { value: 'subscription', label: 'Subscriptions' },
    { value: 'other', label: 'Other' }
  ];

  if (loading && !dashboard) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-module">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Finance Module</h2>
          <p className="text-slate-500 text-sm">Track salaries, expenses, revenue & profit/loss</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Month Selector */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <button 
              onClick={() => handleMonthChange(-1)}
              className="p-2 hover:bg-white rounded-md transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 font-medium text-slate-700 min-w-[140px] text-center">
              {getMonthName(selectedMonth)}
            </span>
            <button 
              onClick={() => handleMonthChange(1)}
              className="p-2 hover:bg-white rounded-md transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <Button onClick={handleDownloadReport} variant="outline" size="sm" data-testid="download-report-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 flex-wrap border-b border-slate-200 pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: Wallet },
          { id: 'salaries', label: 'Salaries', icon: Users },
          { id: 'incentives', label: 'Incentives', icon: TrendingUp },
          { id: 'expenses', label: 'Expenses', icon: Receipt },
          { id: 'revenue', label: 'Revenue', icon: IndianRupee },
          { id: 'charges', label: 'Candidate Charges', icon: FileText }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id)}
            data-testid={`finance-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeView === tab.id
                ? 'bg-violet-100 text-violet-700'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview View */}
      {activeView === 'overview' && summary && (
        <div className="space-y-6">
          {/* Profit/Loss Banner */}
          <div className={`p-6 rounded-2xl ${summary.profit_loss.is_profitable ? 'bg-gradient-to-r from-emerald-500 to-emerald-600' : 'bg-gradient-to-r from-red-500 to-red-600'} text-white`}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <p className="text-white/80 text-sm mb-1">{getMonthName(selectedMonth)}</p>
                <h3 className="text-3xl font-bold flex items-center gap-3">
                  {summary.profit_loss.is_profitable ? (
                    <>
                      <TrendingUp className="w-8 h-8" />
                      PROFIT: {formatCurrency(summary.profit_loss.amount)}
                    </>
                  ) : (
                    <>
                      <TrendingDown className="w-8 h-8" />
                      LOSS: {formatCurrency(Math.abs(summary.profit_loss.amount))}
                    </>
                  )}
                </h3>
                <p className="text-white/80 mt-1">Margin: {summary.profit_loss.margin_percentage}%</p>
              </div>
              <div className="flex items-center gap-2">
                {summary.profit_loss.is_profitable ? (
                  <CheckCircle className="w-12 h-12 text-white/80" />
                ) : (
                  <AlertTriangle className="w-12 h-12 text-white/80" />
                )}
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <IndianRupee className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-slate-500 text-sm">Gross Revenue</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(summary.revenue.gross_revenue)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-slate-500 text-sm">Total Salaries</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(summary.costs.salaries)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Receipt className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-slate-500 text-sm">Expenses</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(summary.costs.operational_expenses)}</p>
            </div>
            <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-violet-600" />
                </div>
                <span className="text-slate-500 text-sm">Incentives Paid</span>
              </div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(summary.costs.incentive_payments)}</p>
            </div>
          </div>

          {/* Suggestions */}
          {summary.suggestions && summary.suggestions.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-5 h-5 text-amber-600" />
                <h4 className="font-semibold text-amber-800">Suggestions</h4>
              </div>
              <ul className="space-y-2">
                {summary.suggestions.map((suggestion, idx) => (
                  <li key={idx} className="text-amber-700 text-sm flex items-start gap-2">
                    <span className="text-amber-400">•</span>
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Revenue vs Costs Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-5 rounded-xl border border-slate-200">
              <h4 className="font-semibold text-slate-700 mb-4">Revenue Breakdown</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Candidate Charges</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(summary.revenue.candidate_charges)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Other Revenue</span>
                  <span className="font-medium text-emerald-600">{formatCurrency(summary.revenue.other_revenue)}</span>
                </div>
                {summary.revenue.deductions > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Deductions (Failed Candidates)</span>
                  <span className="font-medium text-green-600">+{formatCurrency(summary.revenue.deductions)}</span>
                </div>
                )}
                {summary.revenue.refunds > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Refunds</span>
                  <span className="font-medium text-red-600">-{formatCurrency(summary.revenue.refunds)}</span>
                </div>
                )}
                <div className="flex justify-between items-center py-2 font-semibold">
                  <span className="text-slate-800">Net Revenue</span>
                  <span className="text-emerald-600">{formatCurrency(summary.revenue.gross_revenue)}</span>
                </div>
              </div>
            </div>
            <div className="bg-white p-5 rounded-xl border border-slate-200">
              <h4 className="font-semibold text-slate-700 mb-4">Cost Breakdown</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Salaries</span>
                  <span className="font-medium text-red-600">{formatCurrency(summary.costs.salaries)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Operational Expenses</span>
                  <span className="font-medium text-red-600">{formatCurrency(summary.costs.operational_expenses)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Incentive Payments</span>
                  <span className="font-medium text-red-600">{formatCurrency(summary.costs.incentive_payments)}</span>
                </div>
                <div className="flex justify-between items-center py-2 font-semibold">
                  <span className="text-slate-800">Total Costs</span>
                  <span className="text-red-600">{formatCurrency(summary.costs.total_costs)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Salaries View */}
      {activeView === 'salaries' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-slate-700">User Salaries</h3>
            <Button onClick={() => setShowSalaryModal(true)} size="sm" data-testid="add-salary-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Salary
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Monthly Salary</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Effective From</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Notes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {salaries.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">No salary records found</td>
                  </tr>
                ) : (
                  salaries.map((salary, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-800">{salary.user_name}</p>
                          <p className="text-xs text-slate-500">{salary.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 font-semibold text-emerald-600">{formatCurrency(salary.monthly_salary)}</td>
                      <td className="px-4 py-3 text-slate-600">{salary.effective_from || '-'}</td>
                      <td className="px-4 py-3 text-slate-500 text-sm">{salary.notes || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            setSalaryForm(salary);
                            setShowSalaryModal(true);
                          }}
                          className="p-1 text-slate-400 hover:text-violet-600 mr-2"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteSalary(salary.user_email)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="bg-violet-50 p-4 rounded-lg flex justify-between items-center">
            <span className="font-medium text-violet-700">Total Monthly Salary Expense</span>
            <span className="text-xl font-bold text-violet-600">
              {formatCurrency(salaries.reduce((sum, s) => sum + (s.monthly_salary || 0), 0))}
            </span>
          </div>
        </div>
      )}

      {/* Incentives View */}
      {activeView === 'incentives' && (
        <div className="space-y-4">
          <h3 className="font-semibold text-slate-700">Lead Specialist Incentives - {getMonthName(selectedMonth)}</h3>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Lead Specialist</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Referrals</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Initial Cleared</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Placements</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Total Incentive</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Paid</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Pending</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {incentivesSummary.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">No lead specialists found</td>
                  </tr>
                ) : (
                  incentivesSummary.map((ls, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-800">{ls.lead_specialist_name}</p>
                          <p className="text-xs text-slate-500">{ls.lead_specialist_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600">{ls.total_referrals}</td>
                      <td className="px-4 py-3 text-center text-blue-600 font-medium">{ls.initial_cleared}</td>
                      <td className="px-4 py-3 text-center text-emerald-600 font-medium">{ls.placements}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{formatCurrency(ls.total_incentive)}</td>
                      <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(ls.paid_amount)}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(ls.pending_amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="text-sm text-slate-500">
            * Initial Cleared: ₹250 | Placement: ₹500
          </div>
        </div>
      )}

      {/* Expenses View */}
      {activeView === 'expenses' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-slate-700">Operational Expenses - {getMonthName(selectedMonth)}</h3>
            <Button onClick={() => { setExpenseForm({ category: 'electricity', description: '', amount: '', month: selectedMonth, notes: '' }); setEditingItem(null); setShowExpenseModal(true); }} size="sm" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Expense
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Notes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {expenses.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">No expenses recorded for this month</td>
                  </tr>
                ) : (
                  expenses.map((exp, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium text-slate-600 capitalize">{exp.category}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{exp.description}</td>
                      <td className="px-4 py-3 text-right font-semibold text-red-600">{formatCurrency(exp.amount)}</td>
                      <td className="px-4 py-3 text-slate-500 text-sm">{exp.notes || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => { setExpenseForm(exp); setEditingItem(exp); setShowExpenseModal(true); }}
                          className="p-1 text-slate-400 hover:text-violet-600 mr-2"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(exp.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="bg-red-50 p-4 rounded-lg flex justify-between items-center">
            <span className="font-medium text-red-700">Total Monthly Expenses</span>
            <span className="text-xl font-bold text-red-600">
              {formatCurrency(expenses.reduce((sum, e) => sum + (e.amount || 0), 0))}
            </span>
          </div>
        </div>
      )}

      {/* Revenue View */}
      {activeView === 'revenue' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-slate-700">Revenue - {getMonthName(selectedMonth)}</h3>
            <Button onClick={() => { setRevenueForm({ source: 'other', description: '', amount: '', month: selectedMonth, notes: '' }); setEditingItem(null); setShowRevenueModal(true); }} size="sm" data-testid="add-revenue-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Revenue
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Source</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Notes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {revenue.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">No revenue recorded for this month</td>
                  </tr>
                ) : (
                  revenue.map((rev, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-emerald-100 rounded text-xs font-medium text-emerald-600 capitalize">{rev.source?.replace('_', ' ')}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{rev.description}</td>
                      <td className="px-4 py-3 text-right font-semibold text-emerald-600">{formatCurrency(rev.amount)}</td>
                      <td className="px-4 py-3 text-slate-500 text-sm">{rev.notes || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => { setRevenueForm(rev); setEditingItem(rev); setShowRevenueModal(true); }}
                          className="p-1 text-slate-400 hover:text-violet-600 mr-2"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRevenue(rev.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="bg-emerald-50 p-4 rounded-lg flex justify-between items-center">
            <span className="font-medium text-emerald-700">Total Monthly Revenue</span>
            <span className="text-xl font-bold text-emerald-600">
              {formatCurrency(revenue.reduce((sum, r) => sum + (r.amount || 0), 0))}
            </span>
          </div>
        </div>
      )}

      {/* Candidate Charges View */}
      {activeView === 'charges' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-slate-700">Candidate Charges - {getMonthName(selectedMonth)}</h3>
            <Button onClick={() => { setChargeForm({ candidate_email: '', candidate_name: '', charge_type: 'registration', amount: '', status: 'pending', notes: '' }); setEditingItem(null); setShowChargeModal(true); }} size="sm" data-testid="add-charge-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Charge
            </Button>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Candidate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Notes</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {candidateCharges.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">No candidate charges for this month</td>
                  </tr>
                ) : (
                  candidateCharges.map((charge, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-slate-800">{charge.candidate_name}</p>
                          <p className="text-xs text-slate-500">{charge.candidate_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-slate-100 rounded text-xs font-medium text-slate-600 capitalize">{charge.charge_type}</span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-800">{formatCurrency(charge.amount)}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          charge.status === 'paid' ? 'bg-emerald-100 text-emerald-600' :
                          charge.status === 'waived' ? 'bg-slate-100 text-slate-600' :
                          'bg-amber-100 text-amber-600'
                        }`}>
                          {charge.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-sm">{charge.notes || '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => { setChargeForm(charge); setEditingItem(charge); setShowChargeModal(true); }}
                          className="p-1 text-slate-400 hover:text-violet-600 mr-2"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCharge(charge.id)}
                          className="p-1 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-emerald-50 p-4 rounded-lg">
              <p className="text-sm text-emerald-600 mb-1">Paid</p>
              <p className="text-xl font-bold text-emerald-700">
                {formatCurrency(candidateCharges.filter(c => c.status === 'paid').reduce((sum, c) => sum + (c.amount || 0), 0))}
              </p>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg">
              <p className="text-sm text-amber-600 mb-1">Pending</p>
              <p className="text-xl font-bold text-amber-700">
                {formatCurrency(candidateCharges.filter(c => c.status === 'pending').reduce((sum, c) => sum + (c.amount || 0), 0))}
              </p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-1">Waived</p>
              <p className="text-xl font-bold text-slate-700">
                {formatCurrency(candidateCharges.filter(c => c.status === 'waived').reduce((sum, c) => sum + (c.amount || 0), 0))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Salary Modal */}
      <Modal isOpen={showSalaryModal} onClose={() => setShowSalaryModal(false)} title="Set User Salary">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Select User</label>
            <select
              value={salaryForm.user_email}
              onChange={(e) => {
                const user = users.find(u => u.email === e.target.value);
                setSalaryForm({ ...salaryForm, user_email: e.target.value, user_name: user?.name || '' });
              }}
              className="w-full border border-slate-200 rounded-lg p-2 bg-white"
            >
              <option value="">Select user...</option>
              {users.map(u => (
                <option key={u.email} value={u.email}>{u.name} ({u.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Monthly Salary (₹)</label>
            <Input
              type="number"
              value={salaryForm.monthly_salary}
              onChange={(e) => setSalaryForm({ ...salaryForm, monthly_salary: parseFloat(e.target.value) || '' })}
              placeholder="e.g., 25000"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Effective From</label>
            <Input
              type="date"
              value={salaryForm.effective_from}
              onChange={(e) => setSalaryForm({ ...salaryForm, effective_from: e.target.value })}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
            <Input
              value={salaryForm.notes}
              onChange={(e) => setSalaryForm({ ...salaryForm, notes: e.target.value })}
              placeholder="Optional notes"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowSalaryModal(false)} className="flex-1">Cancel</Button>
            <Button onClick={handleSaveSalary} className="flex-1" data-testid="save-salary-btn">
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Expense Modal */}
      <Modal isOpen={showExpenseModal} onClose={() => setShowExpenseModal(false)} title={`${editingItem ? 'Edit' : 'Add'} Expense`}>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Category</label>
            <select
              value={expenseForm.category}
              onChange={(e) => setExpenseForm({ ...expenseForm, category: e.target.value })}
              className="w-full border border-slate-200 rounded-lg p-2 bg-white"
            >
              {expenseCategories.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
            <Input
              value={expenseForm.description}
              onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
              placeholder="e.g., Monthly electricity bill"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Amount (₹)</label>
            <Input
              type="number"
              value={expenseForm.amount}
              onChange={(e) => setExpenseForm({ ...expenseForm, amount: parseFloat(e.target.value) || '' })}
              placeholder="e.g., 5000"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
            <Input
              value={expenseForm.notes}
              onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
              placeholder="Optional notes"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowExpenseModal(false)} className="flex-1">Cancel</Button>
            <Button onClick={handleSaveExpense} className="flex-1" data-testid="save-expense-btn">
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Revenue Modal */}
      <Modal isOpen={showRevenueModal} onClose={() => setShowRevenueModal(false)} title={`${editingItem ? 'Edit' : 'Add'} Revenue`}>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Source</label>
            <select
              value={revenueForm.source}
              onChange={(e) => setRevenueForm({ ...revenueForm, source: e.target.value })}
              className="w-full border border-slate-200 rounded-lg p-2 bg-white"
            >
              {revenueSources.map(src => (
                <option key={src.value} value={src.value}>{src.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Description</label>
            <Input
              value={revenueForm.description}
              onChange={(e) => setRevenueForm({ ...revenueForm, description: e.target.value })}
              placeholder="e.g., Placement fee from XYZ Corp"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Amount (₹)</label>
            <Input
              type="number"
              value={revenueForm.amount}
              onChange={(e) => setRevenueForm({ ...revenueForm, amount: parseFloat(e.target.value) || '' })}
              placeholder="e.g., 50000"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
            <Input
              value={revenueForm.notes}
              onChange={(e) => setRevenueForm({ ...revenueForm, notes: e.target.value })}
              placeholder="Optional notes"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowRevenueModal(false)} className="flex-1">Cancel</Button>
            <Button onClick={handleSaveRevenue} className="flex-1" data-testid="save-revenue-btn">
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </div>
        </div>
      </Modal>

      {/* Charge Modal */}
      <Modal isOpen={showChargeModal} onClose={() => { setShowChargeModal(false); setCandidateSearch(''); setShowCandidateDropdown(false); }} title={`${editingItem ? 'Edit' : 'Add'} Candidate Charge`}>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Candidate Search */}
          <div className="relative">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Search Candidate</label>
            <Input
              value={candidateSearch}
              onChange={(e) => {
                setCandidateSearch(e.target.value);
                setShowCandidateDropdown(true);
              }}
              onFocus={() => setShowCandidateDropdown(true)}
              placeholder="Search by name, email or phone..."
            />
            {showCandidateDropdown && candidateSearch && filteredCandidates.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredCandidates.map((c, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSelectCandidate(c)}
                    className="px-3 py-2 hover:bg-violet-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                  >
                    <p className="font-medium text-slate-800">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.email} {c.phone && `| ${c.phone}`}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Candidate Info */}
          {chargeForm.candidate_name && (
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-slate-700">Selected Candidate:</p>
              <p className="text-slate-800">{chargeForm.candidate_name}</p>
              <p className="text-xs text-slate-500">{chargeForm.candidate_email}</p>
              {chargeForm.candidate_phone && <p className="text-xs text-slate-500">Phone: {chargeForm.candidate_phone}</p>}
            </div>
          )}

          {/* Manual Entry Option */}
          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs text-slate-500 mb-2">Or enter manually:</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Name</label>
                <Input
                  value={chargeForm.candidate_name}
                  onChange={(e) => setChargeForm({ ...chargeForm, candidate_name: e.target.value })}
                  placeholder="Candidate name"
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Phone</label>
                <Input
                  value={chargeForm.candidate_phone}
                  onChange={(e) => setChargeForm({ ...chargeForm, candidate_phone: e.target.value })}
                  placeholder="Phone number"
                  className="text-sm"
                />
              </div>
            </div>
            <div className="mt-2">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Email</label>
              <Input
                type="email"
                value={chargeForm.candidate_email}
                onChange={(e) => setChargeForm({ ...chargeForm, candidate_email: e.target.value })}
                placeholder="candidate@example.com"
                className="text-sm"
              />
            </div>
          </div>

          {/* Charge Type */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Charge Type</label>
            <select
              value={chargeForm.charge_type}
              onChange={(e) => setChargeForm({ ...chargeForm, charge_type: e.target.value })}
              className="w-full border border-slate-200 rounded-lg p-2 bg-white"
            >
              <option value="initial_charge">Initial Charge</option>
              <option value="final_charge">Final Charge</option>
              <option value="deduction">Deduction (Candidate Failed)</option>
              <option value="refund">Refund</option>
              <option value="registration">Registration Fee</option>
              <option value="placement">Placement Fee</option>
              <option value="training">Training Fee</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              Amount (₹) 
              {chargeForm.charge_type === 'refund' && <span className="text-red-500 ml-1">(Refund - counted as loss)</span>}
              {chargeForm.charge_type === 'deduction' && <span className="text-green-500 ml-1">(Deduction - adds to profit)</span>}
            </label>
            <Input
              type="number"
              value={chargeForm.amount}
              onChange={(e) => setChargeForm({ ...chargeForm, amount: parseFloat(e.target.value) || '' })}
              placeholder={chargeForm.charge_type === 'refund' ? "Refund amount (loss)" : chargeForm.charge_type === 'deduction' ? "Deduction amount" : "e.g., 500"}
              className={chargeForm.charge_type === 'refund' ? 'border-red-200 bg-red-50' : chargeForm.charge_type === 'deduction' ? 'border-green-200 bg-green-50' : ''}
            />
          </div>

          {/* Status */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
            <select
              value={chargeForm.status}
              onChange={(e) => setChargeForm({ ...chargeForm, status: e.target.value })}
              className="w-full border border-slate-200 rounded-lg p-2 bg-white"
            >
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="waived">Waived</option>
              {chargeForm.charge_type === 'refund' && <option value="refunded">Refunded</option>}
              {chargeForm.charge_type === 'deduction' && <option value="deducted">Deducted</option>}
            </select>
          </div>

          {/* Additional Info */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Additional Information</label>
            <textarea
              value={chargeForm.additional_info}
              onChange={(e) => setChargeForm({ ...chargeForm, additional_info: e.target.value })}
              placeholder="Any additional details about the candidate or charge..."
              className="w-full border border-slate-200 rounded-lg p-2 text-sm min-h-[60px] resize-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
            <Input
              value={chargeForm.notes}
              onChange={(e) => setChargeForm({ ...chargeForm, notes: e.target.value })}
              placeholder="Optional notes"
            />
          </div>

          <div className="flex gap-2 pt-2 sticky bottom-0 bg-white">
            <Button variant="outline" onClick={() => { setShowChargeModal(false); setCandidateSearch(''); }} className="flex-1">Cancel</Button>
            <Button onClick={handleSaveCharge} className="flex-1" data-testid="save-charge-btn">
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FinanceModule;
